import {
  GeneralInputOption,
  usePulldownOption,
  usePulldownOptions,
} from '@/components/GeneralInputs/hooks/usePulldownOptions';
import {
  PulldownTypeSelect,
  PulldownTypeSelectProps,
} from '@/components/GeneralInputs/PulldownTypeSelect';
import {
  useGetApInvoiceTransactionGeneralInputOptions,
  useGetTransactionItemPulldownOption,
} from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { optionsCache } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Components/CustomTransactionItems/PulldownType/useFindPulldownOption';
import {
  PaymentRequestForm,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import { useModelsToOptionsWithData } from '@/hooks/useTypeToOptions';
import { SelectProps, Skeleton } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { memo, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import styles from './index.module.scss';

type Props<TFieldValues extends PaymentRequestForm> =
  PulldownTypeSelectProps<TFieldValues> & {
    property: TransactionProperty;
  };

const cx = classnames.bind(styles);

const InnerPulldownTypeCell = <TFieldValues extends PaymentRequestForm>({
  control,
  inputId,
  property,
  name,
  rules,
}: Props<TFieldValues>) => {
  const getGeneralInputOptions =
    useGetApInvoiceTransactionGeneralInputOptions();
  const getGeneralInputOption = useGetTransactionItemPulldownOption();

  const {
    optionList,
    isLoading,
    error,
    onSearch,
    fetchMore,
    handleDropdownVisibleChange,
    query,
    menuIsOpen,
  } = usePulldownOptions({
    propertyId: property.id,
    per: 25,
    getGeneralInputOptions,
  });

  if (error) throw error;

  const optionIdValue = useWatch({
    control,
    name: name,
  });

  const optionId = optionIdValue ? String(optionIdValue) : undefined;

  const hasOption = useMemo(
    () =>
      !!optionList.find((option: GeneralInputOption) => option.id === optionId),
    [optionList, optionId]
  );

  const {
    defaultOption,
    isLoading: isLoadingDefaultOption,
    error: errorDefaultOption,
  } = usePulldownOption({
    id: optionId,
    propertyId: property.id,
    hasOption: hasOption,
    getGeneralInputOption,
    optionsCache,
  });

  const memoizedDefaultOption = useMemo(() => {
    if (isLoading) return [];
    if (defaultOption.length > 0) return defaultOption;
    // Option was deleted - return placeholder with empty label to display as empty
    // while preserving the original form value, but only when dropdown is closed
    if (optionId && !hasOption && !menuIsOpen) {
      return [{ value: optionId, label: '', caption: '' }];
    }
    return [];
  }, [isLoading, defaultOption, optionId, hasOption, menuIsOpen]);

  if (errorDefaultOption) throw errorDefaultOption;

  const options = useModelsToOptionsWithData<GeneralInputOption>(
    optionList,
    query,
    memoizedDefaultOption,
    {
      labelKeys: ['label'],
      caption: 'code',
    },
    true
  );

  const selectProps: SelectProps = useMemo(
    () => ({
      selectAll: undefined,
      selectAllLabel: undefined,
      options: options ?? [],
      loading: isLoading,
      onDropdownVisibleChange: handleDropdownVisibleChange,
      onSearch: onSearch,
      fetchMore: fetchMore,
    }),
    [options, isLoading, handleDropdownVisibleChange, onSearch, fetchMore]
  );
  return (
    <>
      {isLoadingDefaultOption ? (
        <div className={cx(styles['loading-container'])}>
          <Skeleton.Input active size='default' block />
        </div>
      ) : (
        <PulldownTypeSelect<TFieldValues>
          control={control}
          inputId={inputId}
          placeholder={property.placeholder}
          name={name}
          rules={rules}
          selectProps={selectProps}
        />
      )}
    </>
  );
};
InnerPulldownTypeCell.displayName = 'InnerPulldownTypeCell';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const PulldownTypeCell = memo(
  InnerPulldownTypeCell
) as typeof InnerPulldownTypeCell;
PulldownTypeCell.displayName = 'PulldownTypeCell';
